import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';
import Shift from 'resources/Shift';

import Schedule from 'features/scheduleBuilder/ScheduleBuilderView/resources/Schedule';

import { captureHydrationException, getSliceState } from '../helpers';
import { ScheduleFetchAction } from '../types';

import { CONFLICT_TYPES, SLICE_NAME } from './constants';
import {
  AvailabilityConflict,
  Conflict,
  ConflictSerializedEntity,
  ConflictsState,
  ShiftConflict,
  TimeOffConflict,
  UnknownConflict,
} from './types';

export const conflictsAdapter = createEntityAdapter<Conflict>({
  selectId: conflict => conflict.id,
});

export const initialState: ConflictsState = {
  ...conflictsAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    removeAll: conflictsAdapter.removeAll,
    removeOne: conflictsAdapter.removeOne,
    removeMany: conflictsAdapter.removeMany,
    setMany: conflictsAdapter.setMany,
  },
  extraReducers: builder => {
    builder.addCase(
      Shift.resourceActionEvent('update'),
      (state, action: any) => {
        if (action?.payload?.included) {
          const conflicts = (
            action?.payload?.included as ConflictSerializedEntity[]
          )
            .filter(
              (entity: ConflictSerializedEntity) =>
                entity.type === 'shiftConflict'
            )
            .map((entity: ConflictSerializedEntity) => ({
              id: entity.id,
              type: 'conflict',
              sourceId: entity.relationships.shift.data.id,
              sourceType: entity.relationships.shift.data.type,
              targetId: entity.relationships.conflict.data.id,
              conflictType: entity.attributes.type,
              targetType: entity.relationships.conflict.data.type,
            }));

          if (conflicts.length > 0) {
            conflictsAdapter.upsertMany(state, conflicts as Conflict[]);
          }
        }
      }
    );
    builder.addCase(
      Shift.resourceActionEvent('create'),
      (state, action: any) => {
        if (action?.payload?.included) {
          const conflicts = (
            action?.payload?.included as ConflictSerializedEntity[]
          )
            .filter(
              (entity: ConflictSerializedEntity) =>
                entity.type === 'shiftConflict'
            )
            .map((entity: ConflictSerializedEntity) => ({
              id: entity.id,
              type: 'conflict',
              sourceId: entity.relationships.shift.data.id,
              sourceType: entity.relationships.shift.data.type,
              targetId: entity.relationships.conflict.data.id,
              conflictType: entity.attributes.type,
              targetType: entity.relationships.conflict.data.type,
            }));

          if (conflicts.length > 0) {
            conflictsAdapter.upsertMany(state, conflicts as Conflict[]);
          }
        }
      }
    );
    builder.addCase(
      Schedule.resourceActionEvent('fetch'),
      (state, action: ScheduleFetchAction) => {
        state.status = 'succeeded';
        if (action.payload?.included) {
          try {
            // Find all conflict types in the included data
            const conflicts = (
              action?.payload?.included as ConflictSerializedEntity[]
            )
              .filter(
                (entity: ConflictSerializedEntity) =>
                  entity.type === 'shiftConflict'
              )
              .map((entity: ConflictSerializedEntity) => {
                // All conflict entities follow the same relationship pattern:
                // - 'shift' relationship is the source entity
                // - 'conflict' relationship is the target entity (polymorphic type)

                // Get the raw conflict type from the API
                const conflictTypeFromAPI = entity.attributes.type;

                // Common base conflict structure
                const baseConflict = {
                  id: entity.id,
                  type: 'conflict',
                  sourceId: entity.relationships.shift.data.id,
                  sourceType: entity.relationships.shift.data.type,
                  targetId: entity.relationships.conflict.data.id,
                  targetType: entity.relationships.conflict.data.type,
                };

                // Convert the API's conflict type to our normalized format
                const normalizedConflictType =
                  CONFLICT_TYPES.fromApiValue(conflictTypeFromAPI);

                // Create the appropriate typed conflict instance based on the normalized type
                switch (normalizedConflictType) {
                  case CONFLICT_TYPES.SHIFT:
                    return {
                      ...baseConflict,
                      conflictType: CONFLICT_TYPES.SHIFT,
                    } as ShiftConflict;

                  case CONFLICT_TYPES.TIME_OFF:
                    return {
                      ...baseConflict,
                      conflictType: CONFLICT_TYPES.TIME_OFF,
                    } as TimeOffConflict;

                  case CONFLICT_TYPES.AVAILABILITY:
                    return {
                      ...baseConflict,
                      conflictType: CONFLICT_TYPES.AVAILABILITY,
                    } as AvailabilityConflict;

                  // For any other conflict types, return a generic conflict
                  default:
                    // Unknown conflict type that will be preserved for future development
                    return {
                      ...baseConflict,
                      conflictType: normalizedConflictType,
                    } as UnknownConflict;
                }
              });

            if (conflicts.length > 0) {
              conflictsAdapter.setAll(state, conflicts);
            }
          } catch (error) {
            captureHydrationException(state, error, SLICE_NAME);
          }
        }
      }
    );
  },
});

export const { removeAll, removeOne, removeMany, setMany } = slice.actions;
export const reducer = slice.reducer;

// Export selectors
export const {
  selectById: selectConflictById,
  selectIds: selectConflictIds,
  selectEntities: selectConflictEntities,
  selectAll: selectAllConflicts,
  selectTotal: selectTotalConflicts,
} = conflictsAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);

const toApiFormat = (conflictType: string): string => {
  if (conflictType === 'timeOff') return 'time_off';
  return conflictType;
};

export const selectShiftConflictType = (
  state: RootState,
  props: { id: string }
): string | undefined => {
  const conflicts = selectAllConflicts(state);
  const shiftConflicts = conflicts.filter(
    c => c.sourceId === props.id && c.sourceType === 'shift'
  );

  if (shiftConflicts.length > 0) {
    return toApiFormat(shiftConflicts[0].conflictType);
  }

  return undefined;
};
